import React, { useState } from "react";
import { CalendarOff, Check, Clock, FileText, LogOut, MessageCircle, Phone, Share } from "lucide-react";
import { Groupe, Ligne, TitreGroupe, Vide, Segments } from "../espace/ui";
import { api, aujourdhui, decaler, erreurDe, chf, jourLisible, majuscule, moisLisible, tel, urlServeur } from "./commun";

/* ── Mes fiches de paie : chaque mois validé par l'école ───────────────────── */
export const Paie = ({ moi }) => {
  const partager = async (d) => {
    const url = urlServeur(d.pdf_lien);
    try {
      const r = await fetch(url);
      const fichier = new File([await r.blob()], `1000ARTS-decompte-${d.mois}.pdf`, { type: "application/pdf" });
      if (navigator.canShare?.({ files: [fichier] })) await navigator.share({ files: [fichier], title: `Décompte ${moisLisible(d.mois)}` });
      else window.open(url, "_blank");
    } catch { /* partage annulé */ }
  };
  if (!moi.paye) return <Vide>Pas de décompte d'heures pour ce compte.</Vide>;
  return (
    <div className="space-y-5" data-testid="prof-paie">
      {(moi.en_cours || []).length > 0 && (
        <section>
          <TitreGroupe>En attente de la fiche</TitreGroupe>
          <Groupe testId="prof-paie-en-cours">
            {moi.en_cours.map((m) => (
              <div key={m.mois} className="flex items-center gap-3 px-4 py-3.5" data-testid={`prof-en-cours-${m.mois}`}>
                <span className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: "#FEF3C7", color: "#B45309" }}><Clock size={18} /></span>
                <div className="flex-1 min-w-0">
                  <div className="text-[15px] font-semibold capitalize">{moisLisible(m.mois)}</div>
                  <div className="text-[12.5px] text-[var(--muted)]">
                    {m.statut === "confirme"
                      ? `Envoyé à l'école${m.envoye_le ? ` le ${m.envoye_le.split("-").reverse().join(".")}` : ""} · en attente de validation`
                      : `${m.nb} cours confirmé${m.nb > 1 ? "s" : ""} pour l'instant`}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[15px] font-semibold tabular-nums">{chf(m.total)}</div>
                  <div className="text-[11.5px] text-[var(--muted)]">{Number(m.heures || 0).toFixed(2)} h</div>
                </div>
              </div>
            ))}
          </Groupe>
        </section>
      )}
      <p className="text-[13.5px] text-[var(--muted)] px-1">
        Ta fiche apparaît ici dès que l'école a validé le mois. Montants bruts (indépendant·e).
      </p>
      {!(moi.decomptes || []).length ? <Vide>Aucune fiche validée pour l'instant.</Vide> : (
        <Groupe>
          {moi.decomptes.map((d) => (
            <div key={d.mois} className="flex items-center gap-3 px-4 py-3.5" data-testid={`prof-fiche-${d.mois}`}>
              <span className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: "#DCFCE7", color: "#15803D" }}><FileText size={18} /></span>
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold capitalize">{moisLisible(d.mois)}</div>
                <div className="text-[12.5px] text-[var(--muted)]">{Number(d.heures || 0).toFixed(2)} h · {chf(d.total)} brut</div>
              </div>
              <a href={urlServeur(d.pdf_lien)} target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs">Ouvrir</a>
              <button onClick={() => partager(d)} aria-label="Partager" className="w-10 h-10 grid place-items-center rounded-full" style={{ background: "var(--primary-tint)", color: "var(--primary)" }}><Share size={16} /></button>
            </div>
          ))}
        </Groupe>
      )}
    </div>
  );
};

/* ── Mes absences ──────────────────────────────────────────────────────────── */
/* Règles de l'école (30.10.2026) : malade → on appelle l'école ; vacances hors vacances
   scolaires → 30 jours avant, avec son remplaçant (externe, payé par l'école) ; autre
   absence → 15 jours avant, avec le motif. */
const VIDE_ABSENCE = { du: "", au: "", type: "vacances", motif: "", cours_id: "",
  remplacant: { prenom: "", nom: "", telephone: "", email: "" } };
export const Absences = ({ moi, onChange }) => {
  const [f, setF] = useState(VIDE_ABSENCE);
  const [message, setMessage] = useState("");
  const [erreur, setErreur] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const telEcole = moi.telephone_ecole || "079 950 40 31";
  const delais = moi.delais_absence || { vacances: 30, autre: 15 };
  const auPlusTot = decaler(aujourdhui(), f.type === "vacances" ? delais.vacances : delais.autre);
  const r = f.remplacant;
  const complet = !!f.du && f.du >= auPlusTot && (f.type === "vacances"
    ? [r.prenom, r.nom, r.telephone, r.email].every((x) => x.trim())
    : !!f.motif.trim());
  const envoyer = async () => {
    if (envoi) return;
    setErreur(""); setMessage(""); setEnvoi(true);
    try {
      const { data } = await api.post("/absence", f.type === "vacances" ? f : { ...f, remplacant: null });
      setMessage(data.message); setF(VIDE_ABSENCE); onChange();
    } catch (e) { setErreur(erreurDe(e)); }
    setEnvoi(false);
  };
  const remp = (k) => (e) => setF({ ...f, remplacant: { ...f.remplacant, [k]: e.target.value } });
  return (
    <div className="space-y-6" data-testid="prof-absences">
      <Groupe>
        <div className="p-4 space-y-3">
          <a href={tel(telEcole)} className="flex items-start gap-3 rounded-xl p-3" style={{ background: "#FEF2F2", color: "#991B1B" }}
            data-testid="prof-absence-malade">
            <Phone size={18} className="shrink-0 mt-0.5" />
            <span className="text-[13.5px] leading-snug"><strong>Malade ou urgence ?</strong> Appelle directement l'école au <strong>{telEcole}</strong> : elle s'occupe du remplacement.</span>
          </a>
          <div className="text-[16px] font-semibold">Annoncer une absence</div>
          <Segments testId="prof-absence-type" valeur={f.type} onChange={(v) => setF({ ...f, type: v })}
            options={[{ id: "vacances", label: "Vacances" }, { id: "autre", label: "Autre" }]} />
          <p className="text-[12.5px] text-[var(--muted)] leading-snug" data-testid="prof-absence-regle">
            {f.type === "vacances"
              ? `Les profs suivent les vacances scolaires. Des vacances en dehors s'annoncent au moins ${delais.vacances} jours à l'avance, avec la personne qui te remplace (l'école la paie directement).`
              : `Une absence s'annonce au moins ${delais.autre} jours à l'avance, avec son motif.`}
          </p>
          <div className="grid grid-cols-2 gap-2 text-[12.5px] text-[var(--muted)]">
            <label>Du<input type="date" value={f.du} min={auPlusTot} onChange={(e) => setF({ ...f, du: e.target.value })} className="input-field w-full mt-1" data-testid="prof-absence-du" /></label>
            <label>Au (inclus)<input type="date" value={f.au} min={f.du} onChange={(e) => setF({ ...f, au: e.target.value })} className="input-field w-full mt-1" /></label>
          </div>
          <select className="input-field w-full" value={f.cours_id} onChange={(e) => setF({ ...f, cours_id: e.target.value })}>
            <option value="">Tous mes cours</option>
            {moi.cours.map((c) => <option key={c.id} value={c.id}>Seulement : {c.nom} ({c.jour} {c.debut})</option>)}
          </select>
          {f.type === "vacances" && (
            <div className="space-y-2" data-testid="prof-absence-remplacant">
              <div className="text-[13px] font-medium">Qui te remplace ?</div>
              <div className="grid grid-cols-2 gap-2">
                <input value={r.prenom} onChange={remp("prenom")} placeholder="Prénom" className="input-field w-full" autoComplete="off" />
                <input value={r.nom} onChange={remp("nom")} placeholder="Nom" className="input-field w-full" autoComplete="off" />
              </div>
              <input value={r.telephone} onChange={remp("telephone")} placeholder="Téléphone" inputMode="tel" className="input-field w-full" autoComplete="off" />
              <input value={r.email} onChange={remp("email")} placeholder="E-mail" inputMode="email" className="input-field w-full" autoComplete="off" />
            </div>
          )}
          <input value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })}
            placeholder={f.type === "autre" ? "Motif (obligatoire)" : "Un mot pour l'école (facultatif)"} className="input-field w-full"
            data-testid="prof-absence-motif" />
          {f.du && f.du < auPlusTot && (
            <p className="text-[12.5px]" style={{ color: "#B45309" }}>Au plus tôt pour le {jourLisible(auPlusTot)}. Pour une urgence, appelle l'école.</p>
          )}
          <button onClick={envoyer} disabled={!complet || envoi} className="btn-primary w-full justify-center" data-testid="prof-absence-envoyer">
            <CalendarOff size={15} /> Prévenir l'école
          </button>
          {message && <p className="text-sm" style={{ color: "#15803D" }}>{message}</p>}
          {erreur && <p className="text-sm" style={{ color: "#B91C1C" }}>{erreur}</p>}
          <p className="text-[12px] text-[var(--muted)]">Tes séances d'absence ne comptent pas dans tes heures.</p>
        </div>
      </Groupe>
      {moi.absences.length > 0 && (
        <section>
          <TitreGroupe>Mes absences annoncées</TitreGroupe>
          <Groupe>
            {moi.absences.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <div className="text-[15px]">{majuscule(a.du === a.au ? jourLisible(a.du) : `${jourLisible(a.du)} → ${jourLisible(a.au)}`)}</div>
                  {/* Pour quel cours, et qui remplace (tests de l'école, 01.11.2026). */}
                  <div className="text-[12.5px] text-[var(--muted)] break-words">
                    {majuscule(a.type)}{a.cours_nom ? ` · ${a.cours_nom}` : " · tous mes cours"}{a.motif ? ` · ${a.motif}` : ""}
                  </div>
                  {(a.remplacant_prenom || a.remplacant_externe) && (
                    <div className="text-[12.5px] break-words" style={{ color: "#15803D" }} data-testid="prof-absence-remplace-par">
                      Remplacé·e par {a.remplacant_prenom || `${a.remplacant_externe.prenom} ${a.remplacant_externe.nom}`}
                    </div>
                  )}
                  {/* Son remplaçant externe : l'école l'appelle, puis le confirme (01.11.2026). */}
                  {a.remplacant_externe && !a.remplacant_id && (a.remplacant_confirme_le ? (
                    <div className="text-[12.5px] font-medium inline-flex items-center gap-1" style={{ color: "#15803D" }} data-testid="prof-absence-confirme">
                      <Check size={13} /> Remplaçant confirmé par l'école
                    </div>
                  ) : (
                    <div className="text-[12.5px]" style={{ color: "#B45309" }} data-testid="prof-absence-a-confirmer">
                      L'école doit encore confirmer ton remplaçant
                    </div>
                  ))}
                </div>
                {/* Un remplaçant de l'équipe compte sur ce cours : seule l'école annule. */}
                {a.remplacant_id ? (
                  <a href={tel(moi.telephone_ecole || "079 950 40 31")} className="shrink-0 text-[12.5px] text-right text-[var(--muted)] leading-snug max-w-[110px]">
                    Pour annuler, appelle l'école
                  </a>
                ) : <button className="shrink-0 text-[13.5px] font-medium min-h-[44px]" style={{ color: "var(--primary)" }}
                  onClick={async () => {
                    // Déjà commencée : elle s'arrête hier (les jours passés restent une absence).
                    const commencee = a.du < aujourdhui();
                    if (!window.confirm(commencee ? "Tu reviens plus tôt ? L'absence s'arrêtera hier." : "Annuler cette absence ?")) return;
                    setErreur(""); setMessage("");
                    try {
                      const { data } = await api.delete(`/absence/${a.id}`);
                      setMessage(data.raccourcie ? "C'est noté : ton absence s'arrête hier, l'école est prévenue." : "Absence annulée, l'école est prévenue.");
                      onChange();
                    } catch (e) { setErreur(erreurDe(e)); }
                  }}>Annuler</button>}
              </div>
            ))}
          </Groupe>
        </section>
      )}
    </div>
  );
};

/* ── Mon profil : mes coordonnées, mes cours ───────────────────────────────── */
const Champ = ({ label, ...props }) => (
  <label className="flex items-center gap-3 px-4 py-2.5">
    <span className="w-[92px] shrink-0 text-[14px] text-[var(--muted)]">{label}</span>
    <input className="flex-1 min-w-0 text-[15px] py-1.5 outline-none bg-transparent" {...props} />
  </label>
);

export const Profil = ({ moi, onChange, onQuitter, choixEspace }) => {
  const p = moi.prof;
  const [f, setF] = useState({ nom_complet: p.nom_complet || "", telephone: p.telephone || "", adresse: p.adresse || "",
    npa: p.npa || "", localite: p.localite || "", iban: p.iban || "", avs: p.avs || "" });
  const [etat, setEtat] = useState("");
  const change = JSON.stringify(f) !== JSON.stringify({ nom_complet: p.nom_complet || "", telephone: p.telephone || "", adresse: p.adresse || "",
    npa: p.npa || "", localite: p.localite || "", iban: p.iban || "", avs: p.avs || "" });
  const enregistrer = async () => {
    setEtat("");
    try { const { data } = await api.put("/profil", f); setEtat(data.message); onChange(); }
    catch (e) { setEtat(erreurDe(e)); }
  };
  const champ = (k) => ({ value: f[k], onChange: (e) => setF({ ...f, [k]: e.target.value }) });
  // « avec Noémie, Roxane, Miller » : ses collègues du cours, sans lui-même (on lisait
  // « avec Noémie, Roxane, Lydia, Miller » dans l'app de Lydia).
  const simple = (t) => String(t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim().split(/\s+/)[0];
  const avec = (profsTxt) => String(profsTxt || "").split(/\s*(?:,|\/|&|\+|·|\s-\s|\set\s)\s*/)
    .filter((n) => n && simple(n) !== simple(p.prenom)).join(", ");
  return (
    <div className="space-y-6" data-testid="prof-profil">
      <section>
        <TitreGroupe>Mes coordonnées</TitreGroupe>
        <Groupe>
          <Champ label="Nom" placeholder="Prénom Nom" {...champ("nom_complet")} data-testid="prof-profil-nom" />
          <Champ label="Téléphone" type="tel" placeholder="079 …" {...champ("telephone")} data-testid="prof-profil-tel" />
          <Champ label="Adresse" placeholder="Rue et numéro" {...champ("adresse")} />
          <Champ label="NPA" inputMode="numeric" placeholder="1053" {...champ("npa")} />
          <Champ label="Localité" placeholder="Cugy" {...champ("localite")} />
          <Champ label="IBAN" placeholder="CH.. .... .... .... .... ." {...champ("iban")} data-testid="prof-profil-iban" />
          <Champ label="N° AVS" placeholder="756.…" {...champ("avs")} />
          <div className="flex items-center gap-3 px-4 py-3">
            <span className="w-[92px] shrink-0 text-[14px] text-[var(--muted)]">E-mail</span>
            <span className="flex-1 text-[15px] text-[var(--muted)] truncate">{p.email}</span>
          </div>
        </Groupe>
        <p className="text-[12px] text-[var(--muted)] px-1 mt-2">L'e-mail sert à te connecter : pour le changer, écris à l'école. L'adresse et l'IBAN figurent sur ton décompte.</p>
        {change && <button className="btn-primary w-full justify-center mt-3" onClick={enregistrer} data-testid="prof-profil-enregistrer"><Check size={15} /> Enregistrer</button>}
        {etat && <p className="text-[13px] mt-2 px-1" style={{ color: etat.includes("enregistré") ? "#15803D" : "#B91C1C" }}>{etat}</p>}
      </section>

      <section>
        <TitreGroupe>Mes cours de la semaine</TitreGroupe>
        <Groupe>
          {moi.cours.map((c) => (
            <div key={c.id} className="flex items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="text-[15px]">{c.nom}</div>
                <div className="text-[12.5px] text-[var(--muted)]">{c.jour} {c.debut}–{c.fin}{c.salle ? ` · salle ${c.salle}` : ""}{avec(c.professeur) ? ` · avec ${avec(c.professeur)}` : ""}</div>
              </div>
              {c.lien_whatsapp && <a href={c.lien_whatsapp} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp"
                className="w-9 h-9 rounded-full grid place-items-center" style={{ background: "#DCFCE7", color: "#15803D" }}><MessageCircle size={16} /></a>}
            </div>
          ))}
        </Groupe>
      </section>

      <Groupe>
        {choixEspace}
        <Ligne Icone={LogOut} couleur="#DC2626" titre="Me déconnecter" danger fleche={false} onClick={onQuitter} testId="prof-deconnexion" />
      </Groupe>
    </div>
  );
};
